import React from "react";
import { Typography } from "@mui/material";
import { Form, Row } from "react-bootstrap";
import { ToggleButton } from "../../../../../components";
import { MARKETPLACE_CONSTANTS } from "../../../../../config/constants";
import { colors } from "../../../../../config/colors";
import { TEXT_ALIGN_LEFT } from "../../../../../config/styles";

/**
 * StockManagement Component
 *
 * Manages stock and tax related settings for products:
 * - Quantity per product (with toggle and input)
 * - Maximum quantity per student (with toggle and input)
 * - Price inclusive of VAT (with toggle, shows input when disabled)
 * - Total selling quantity (always visible input)
 */
const StockManagement = ({
  stockControlEnabled,
  availableStock,
  purchaseLimitEnabled,
  maxQuantityPerStudent,
  priceInclusiveVatEnabled,
  vatPercentage,
  onChange,
  hasError = {},
}) => {
  const handleIntegerFieldChange = (fieldName, value) => {
    if (value === "" || (/^\d+$/.test(value) && value.length <= 5)) {
      onChange(fieldName, value);
    }
  };

  const handleIntegerFieldKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div class="w-100">
      {/* 2. Quantity Per Product */}
      <div className="d-flex w-100 justify-content-between align-items-center mb-2">
        <div>
          <Typography variant="h5" className="mb-1">
            {MARKETPLACE_CONSTANTS.QUANTITY_PER_PRODUCT ||
              "Stock Control"}
          </Typography>
          <Typography variant="h4">
            {MARKETPLACE_CONSTANTS.QUANTITY_PER_PRODUCT_DETAILS ||
              "Enable to set available stock for this product"}
          </Typography>
        </div>
        <div className="d-flex flex-wrap" style={{ marginTop: 8 }}>
          <div>
            <ToggleButton
              isChecked={stockControlEnabled}
              onClick={() => onChange("quantityPerProductToggle")}
            />
          </div>
        </div>
      </div>
      {stockControlEnabled && (
        <>
          <Form.Floating
            as={Row}
            className="mb-1 mx-1"
            controlId="availableStock"
          >
            <Form.Control
              type="number"
              placeholder={
                MARKETPLACE_CONSTANTS.QUANTITY_PER_PRODUCT ||
                "Available Stock"
              }
              value={availableStock}
              onChange={(e) =>
                handleIntegerFieldChange("availableStock", e.target.value)
              }
              onKeyDown={handleIntegerFieldKeyDown}
              min="0"
              step="1"
            />
            <label htmlFor="availableStock">
              <span>
                {MARKETPLACE_CONSTANTS.QUANTITY_PER_PRODUCT ||
                  "Available Stock"}
              </span>
            </label>
          </Form.Floating>
          {hasError.availableStock ? (
            <div
              className="mb-3 mx-1 w-100"
              style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
            >
              {hasError.availableStock}
            </div>
          ) : (
            <Typography
              variant="subtitle2"
              className="mx-1 mb-3 w-100"
              sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
            >
              {MARKETPLACE_CONSTANTS.QUANTITY_PER_PRODUCT_HELPER ||
                "Enter the quantity available per product"}
            </Typography>
          )}
        </>
      )}

      {/* 3. Maximum Quantity Per Student */}
      <div className="d-flex w-100 justify-content-between align-items-center mb-2  mt-3">
        <div>
          <Typography variant="h5" className="mb-1">
            {MARKETPLACE_CONSTANTS.MAX_QUANTITY_PER_STUDENT ||
              "Purchase Limit"}
          </Typography>
          <Typography variant="h4">
            {MARKETPLACE_CONSTANTS.MAX_QUANTITY_PER_STUDENT_DETAILS ||
              "Enable to limit quantity each student can purchase"}
          </Typography>
        </div>
        <div className="d-flex flex-wrap" style={{ marginTop: 8 }}>
          <div>
            <ToggleButton
              isChecked={purchaseLimitEnabled}
              onClick={() => onChange("maxQuantityPerStudentToggle")}
            />
          </div>
        </div>
      </div>
      {purchaseLimitEnabled && (
        <>
          <Form.Floating
            as={Row}
            className="mb-1 mx-1"
            controlId="maxQuantityPerStudent"
          >
            <Form.Control
              type="number"
              placeholder={
                MARKETPLACE_CONSTANTS.MAX_QUANTITY_PER_STUDENT ||
                "Maximum Quantity Per Student"
              }
              value={maxQuantityPerStudent}
              onChange={(e) =>
                handleIntegerFieldChange(
                  "maxQuantityPerStudent",
                  e.target.value
                )
              }
              onKeyDown={handleIntegerFieldKeyDown}
              min="0"
              step="1"
            />
            <label htmlFor="maxQuantityPerStudent">
              <span>
                {MARKETPLACE_CONSTANTS.MAX_QUANTITY_PER_STUDENT ||
                  "Maximum Quantity Per Student"}
              </span>
            </label>
          </Form.Floating>
          {hasError.maxQuantityPerStudent ? (
            <div
              className="mb-3 mx-1 w-100"
              style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
            >
              {hasError.maxQuantityPerStudent}
            </div>
          ) : (
            <Typography
              variant="subtitle2"
              className="mx-1 mb-3 w-100"
              sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
            >
              {MARKETPLACE_CONSTANTS.MAX_QUANTITY_PER_STUDENT_HELPER ||
                "Maximum quantity a student can purchase"}
            </Typography>
          )}
        </>
      )}

      {/* 4. Price Inclusive of VAT */}
      <div className="d-flex w-100 justify-content-between align-items-center mb-2  mt-3">
        <div>
          <Typography variant="h5" className="mb-1">
            {MARKETPLACE_CONSTANTS.PRICE_INCLUSIVE_VAT ||
              "Price Inclusive of VAT"}
          </Typography>
          <Typography variant="h4">
            {MARKETPLACE_CONSTANTS.PRICE_INCLUSIVE_VAT_DETAILS ||
              "Product price includes VAT"}
          </Typography>
        </div>
        <div className="d-flex flex-wrap" style={{ marginTop: 8 }}>
          <div>
            <ToggleButton
              isChecked={priceInclusiveVatEnabled}
              onClick={() => onChange("priceInclusiveVatToggle")}
            />
          </div>
        </div>
      </div>
        <>
          <Form.Floating
            as={Row}
            className="mb-1 mx-1"
            controlId="vatPercentage"
          >
            <Form.Control
              type="number"
              placeholder={
                MARKETPLACE_CONSTANTS.VAT_PERCENTAGE || "VAT Percentage"
              }
              value={vatPercentage}
              onChange={(e) => onChange("vatPercentage", e.target.value)}
              min="0"
              max="100"
            />
            <label htmlFor="vatPercentage">
              <span>
                {MARKETPLACE_CONSTANTS.VAT_PERCENTAGE || "VAT Percentage (%)"}
              </span>
            </label>
          </Form.Floating>
          {hasError.vatPercentage ? (
            <div
              className="mb-3 mx-1 w-100"
              style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
            >
              {hasError.vatPercentage}
            </div>
          ) : (
            <Typography
              variant="subtitle2"
              className="mx-1 mb-3 w-100"
              sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
            >
              {MARKETPLACE_CONSTANTS.VAT_PERCENTAGE_HELPER ||
                "Enter VAT percentage (0-100)"}
            </Typography>
          )}
        </>
    </div>
  );
};

export default StockManagement;
